/**
 * seo.ts — keyword data from DataForSEO (Google Ads search volume), stored as evidence.
 *
 * Facts, from DataForSEO's own docs (docs.dataforseo.com, Keywords Data → Google Ads → Search Volume, Live):
 *   • POST /v3/keywords_data/google_ads/search_volume/live, HTTP Basic auth with the API login and password;
 *   • up to 1,000 keywords per request, each up to 80 characters and 10 words, lower-cased by the service;
 *   • charged per request, not per keyword: $0.09 for up to 1,000 keywords;
 *   • Google Ads Live endpoints allow at most 12 requests a minute per account;
 *   • `search_volume` is null when Google has no data, so it is stored as "not available", never 0.
 * The number is Google Ads' average monthly searches for the last 12 months: a third-party estimate, labelled "estimated".
 */
import { z } from "zod";

export const DATAFORSEO_LIMITS = {
  costPerRequestUsd: 0.09,
  maxKeywordsPerRequest: 1000,
  maxKeywordChars: 80,
  maxKeywordWords: 10,
  /** How many keywords one refresh from the CMS may send. Small on purpose: each result is a database row, and the Free plan allows 50 database queries per request. Raise it after moving to the paid plan. */
  maxKeywordsPerRefresh: 100,
  defaultMonthlyCapUsd: 5,
  maxMonthlyCapUsd: 1000,
} as const;

/** Google location ids used by DataForSEO. "worldwide" sends no location. */
export const SEO_LOCATIONS = {
  us: { label: "United States", code: 2840 },
  uk: { label: "United Kingdom", code: 2826 },
  ca: { label: "Canada", code: 2124 },
  au: { label: "Australia", code: 2036 },
  in: { label: "India", code: 2356 },
  pk: { label: "Pakistan", code: 2586 },
  ae: { label: "United Arab Emirates", code: 2784 },
  worldwide: { label: "Worldwide", code: null },
} as const;
export type SeoLocationKey = keyof typeof SEO_LOCATIONS;
export const seoLocationKeySchema = z.enum(Object.keys(SEO_LOCATIONS) as [SeoLocationKey, ...SeoLocationKey[]]);

export interface KeywordProblem {
  keyword: string;
  reason: string;
}

/** Lower-cases, trims, collapses spaces and removes duplicates; reports anything DataForSEO would reject. */
export function normalizeKeywords(raw: readonly string[]): { keywords: string[]; problems: KeywordProblem[] } {
  const seen = new Set<string>();
  const keywords: string[] = [];
  const problems: KeywordProblem[] = [];
  for (const item of raw) {
    const k = item.normalize("NFKC").toLowerCase().replace(/\s+/g, " ").trim();
    if (!k || seen.has(k)) continue;
    seen.add(k);
    if (k.length > DATAFORSEO_LIMITS.maxKeywordChars) problems.push({ keyword: k.slice(0, 40) + "…", reason: `longer than ${DATAFORSEO_LIMITS.maxKeywordChars} characters` });
    else if (k.split(" ").length > DATAFORSEO_LIMITS.maxKeywordWords) problems.push({ keyword: k, reason: `more than ${DATAFORSEO_LIMITS.maxKeywordWords} words` });
    else if (/[^\p{L}\p{N}\s\-'&+.]/u.test(k)) problems.push({ keyword: k, reason: "contains a symbol the service does not accept" });
    else keywords.push(k);
  }
  return { keywords, problems };
}

export const keywordRefreshBodySchema = z.object({
  siteId: z.string().min(1).max(100),
  keywords: z.array(z.string().max(300)).min(1).max(500),
  location: seoLocationKeySchema.default("us"),
  languageCode: z.string().regex(/^[a-z]{2}(-[A-Z]{2})?$/).default("en"),
});
export type KeywordRefreshBody = z.infer<typeof keywordRefreshBodySchema>;

export const keywordRefreshResultSchema = z.object({
  runId: z.string(),
  status: z.enum(["succeeded", "partial", "failed"]),
  requested: z.number().int(),
  stored: z.number().int(),
  withoutData: z.number().int(),
  skipped: z.array(z.object({ keyword: z.string(), reason: z.string() })),
  costUsd: z.number(),
  error: z.string().nullable(),
});
export type KeywordRefreshResult = z.infer<typeof keywordRefreshResultSchema>;

export const seoStatusSchema = z.object({
  /** Login and password are both saved in Admin → API keys. */
  configured: z.boolean(),
  evidenceEnabled: z.boolean(),
  month: z.string(),
  spentUsd: z.number(),
  capUsd: z.number(),
  remainingUsd: z.number(),
  costPerRequestUsd: z.number(),
  maxKeywordsPerRefresh: z.number().int(),
});
export type SeoStatus = z.infer<typeof seoStatusSchema>;
